import { lazy } from "react";

/**
 * Agent screens, looked up by `agentKey` (design §13.1).
 *
 * An entitled agent with no client module yet renders a "coming soon" tile rather than a
 * broken route, so the backend can ship an agent before the frontend does.
 */
export const AGENT_VIEWS = {
  "accident-prevention": lazy(() =>
    import("../agents/accidentPrevention/AgentPage")
  ),
};

export const getAgentView = (agentKey) => AGENT_VIEWS[agentKey] || null;

/**
 * AskMila's own address — the front door of the product, and where the shell sends anything it
 * cannot place: a path that names no agent, or an agent this user has no grant for.
 */
export const ASKMILA_PATH = "/mila";

/**
 * The address each agent is reached at, by `agentKey` (design §13.1).
 *
 * Deliberately a separate fact from the key. `agentKey` is stored data — grants, audit rows and
 * the registry table all carry it, and this one still reads `accident-prevention` from before
 * the agent was renamed — while the path is read aloud, bookmarked, and pasted to a colleague.
 * Renaming the agent moved its address and left every stored key exactly where it was.
 *
 * An agent with no row here is addressed by its own key, so an agent shipped backend-first is
 * reachable and bookmarkable before this file has heard of it.
 */
export const AGENT_PATH_SLUGS = {
  "accident-prevention": "incident-prevention-agent",
};

const AGENT_KEYS_BY_SLUG = Object.fromEntries(
  Object.entries(AGENT_PATH_SLUGS).map(([agentKey, slug]) => [slug, agentKey])
);

/** Where an agent lives. The one function that turns a key into a URL. */
export const getAgentPath = (agentKey) => `/${AGENT_PATH_SLUGS[agentKey] || agentKey}`;

/**
 * The agent a path names — or `null` for AskMila, which is every path that names no agent,
 * the empty one included.
 *
 * An unrecognised slug comes back as a key rather than as null on purpose: only the caller
 * knows which agents this user may see, and "no such agent" and "an agent you have no grant
 * for" want the same answer from it. Lower-cased and stripped of its slashes first, so a
 * hand-typed `/Incident-Prevention-Agent/` still arrives somewhere.
 */
export const getAgentKeyForPath = (pathname) => {
  const slug = String(pathname || "")
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();

  if (!slug || slug === ASKMILA_PATH.slice(1)) return null;

  return AGENT_KEYS_BY_SLUG[slug] || slug;
};
